import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import type { ReactElement } from 'react'

import {
  PlanEditor,
  plansQuery,
  useCreatePlan,
  useUpdatePlan,
} from '~/features/subscriptions'
import { logger } from '~/lib/logger'

/**
 * `/admin/subscriptions/plans/$planId` — plan editor route (HANDOFF §3).
 *
 * The path param `new` is treated as a sentinel for create mode; any
 * other value is looked up in the cached plans list. The editor itself
 * owns 4-tab form state (Identity · Pricing · Modules · Stripe) and a
 * sticky live-preview column.
 */
export const Route = createFileRoute(
  '/admin/subscriptions/plans/$planId',
)({
  component: PlanEditorPage,
})

function PlanEditorPage(): ReactElement {
  const { planId } = Route.useParams()
  const isCreate = planId === 'new'
  const navigate = useNavigate()

  const plans = useQuery(plansQuery())
  const createMutation = useCreatePlan()
  const updateMutation = useUpdatePlan()

  const initial = isCreate
    ? undefined
    : plans.data?.plans.find((p) => p.id === planId)

  if (!isCreate && plans.isLoading) {
    return <Skeleton />
  }
  if (!isCreate && !initial && plans.isSuccess) {
    return (
      <div className="ka-subs-list-page ka-subs-editor-page">
        <div className="ka-card ka-subs-empty-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Plan not found</div>
            <div className="ka-empty-body">
              This plan may have been archived or deleted.
            </div>
            <div className="ka-empty-actions">
              <Link
                to="/admin/subscriptions/plans"
                className="ka-btn ka-btn--ghost"
              >
                Back to plans
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (
    payload:
      | {
          mode: 'create'
          values: Parameters<typeof createMutation.mutate>[0]
        }
      | {
          mode: 'edit'
          planId: string
          values: Parameters<typeof updateMutation.mutate>[0]['patch']
        },
  ): void => {
    if (payload.mode === 'create') {
      createMutation.mutate(payload.values, {
        onSuccess: () => {
          void navigate({ to: '/admin/subscriptions/plans' })
        },
        onError: (cause) => logger.warn('plan create failed', { cause }),
      })
    } else {
      updateMutation.mutate(
        { planId: payload.planId, patch: payload.values },
        {
          onSuccess: () => {
            void navigate({ to: '/admin/subscriptions/plans' })
          },
          onError: (cause) => logger.warn('plan update failed', { cause }),
        },
      )
    }
  }

  return (
    <div className="ka-subs-list-page ka-subs-editor-page">
      <header className="ka-page-header">
        <div>
          <nav aria-label="Breadcrumb" className="ka-breadcrumb">
            <Link to="/admin/subscriptions/plans" className="ka-breadcrumb-item">
              Plans
            </Link>
            <ChevronRight size={12} aria-hidden="true" className="ka-breadcrumb-sep" />
            <span className="ka-breadcrumb-item">
              {isCreate ? 'New plan' : (initial?.display_name ?? initial?.name ?? planId)}
            </span>
          </nav>
          <h1 className="ka-page-header-title">
            {isCreate ? 'New plan' : initial?.display_name ?? initial?.name}
          </h1>
        </div>
      </header>

      <PlanEditor
        initial={initial}
        isSubmitting={isSubmitting}
        error={isCreate ? createMutation.error : updateMutation.error}
        onCancel={() =>
          void navigate({ to: '/admin/subscriptions/plans' })
        }
        onSubmit={handleSubmit}
      />
    </div>
  )
}

function Skeleton(): ReactElement {
  return (
    <div className="ka-subs-list-page ka-subs-editor-page" aria-busy="true">
      <div className="ka-card ka-card-body--lg ka-subs-empty-card">
        <div className="ka-sk ka-sk--heading ka-sk--mb" />
        <div className="ka-sk ka-sk--tall" />
      </div>
    </div>
  )
}
